import { Injectable, InjectionToken } from '@angular/core';
import { environment } from '@env/environment';

/**
 * true solo en environment.mock.ts: am-kiosk-mock-server no cifra, así que el
 * interceptor deja pasar los requests sin cifrar ni descifrar.
 */
export const ENCRYPTION_BYPASS = new InjectionToken<boolean>('ENCRYPTION_BYPASS', {
  providedIn: 'root',
  factory: () => environment.publics?.bypass === true,
});

export type EncryptionErrorCode =
  | 'KEY_MISSING'
  | 'KEY_INVALID'
  | 'ENCRYPT_FAILED'
  | 'DECRYPT_FAILED'
  | 'CRYPTO_UNAVAILABLE'
  | 'RSA_KEYGEN_FAILED'
  | 'RSA_ENCRYPT_FAILED'
  | 'RSA_DECRYPT_FAILED';

export class EncryptionError extends Error {
  constructor(public readonly code: EncryptionErrorCode, message: string) {
    super(message);
    this.name = 'EncryptionError';
  }
}

/** Llave AES-256 e IV de un solo uso, en Base64. */
export interface AesMaterial {
  keyB64: string;
  ivB64: string;
}

const ALGORITHM = 'AES-CBC';
/** WebCrypto (Chromium) solo soporta AES-128 y AES-256. */
const VALID_KEY_BYTES = [16, 32];
const IV_BYTES = 16;
const BASE64_PATTERN = /^[A-Za-z0-9+/]+={0,2}$/;

/**
 * Parte simétrica del esquema RSA híbrido (el mismo de CUSS): AES-256-CBC/PKCS7
 * con una llave e IV aleatorios de un solo uso por mensaje. La llave viaja
 * cifrada con RSA (ver RsaKeyService); aquí no hay llaves estáticas.
 *
 * Lo consume ApiEncryptionInterceptor; los servicios funcionales no lo usan
 * directamente. Nunca registra en logs el texto plano, el cifrado ni la llave.
 */
@Injectable({
  providedIn: 'root',
})
export class EncryptionService {
  /** Genera una llave AES-256 e IV nuevos; cada request usa los suyos. */
  generateAesMaterial(): AesMaterial {
    const crypto = requireCrypto();
    return {
      keyB64: bytesToBase64(crypto.getRandomValues(new Uint8Array(32))),
      ivB64: bytesToBase64(crypto.getRandomValues(new Uint8Array(IV_BYTES))),
    };
  }

  /**
   * Cifra un texto (normalmente un JSON) con la llave e IV dados.
   * @returns el cifrado en Base64.
   */
  async encryptWithKey(plainText: string, keyB64: string, ivB64: string): Promise<string> {
    const key = await importAesKey(keyB64, 'encrypt');
    const iv = decodeIv(ivB64);
    try {
      const encrypted = await requireCrypto().subtle.encrypt(
        { name: ALGORITHM, iv },
        key,
        new TextEncoder().encode(plainText)
      );
      return bytesToBase64(new Uint8Array(encrypted));
    } catch {
      throw new EncryptionError('ENCRYPT_FAILED', 'No se pudo cifrar el payload');
    }
  }

  /**
   * Descifra un texto Base64 con la llave e IV dados.
   * @returns el texto plano UTF-8.
   */
  async decryptWithKey(cipherText: string, keyB64: string, ivB64: string): Promise<string> {
    const key = await importAesKey(keyB64, 'decrypt');
    const iv = decodeIv(ivB64);
    const cleaned = cleanBase64(cipherText);
    if (!cleaned || !BASE64_PATTERN.test(cleaned)) {
      throw new EncryptionError('DECRYPT_FAILED', 'La respuesta no es un Base64 válido');
    }
    try {
      const decrypted = await requireCrypto().subtle.decrypt(
        { name: ALGORITHM, iv },
        key,
        base64ToBytes(cleaned)
      );
      return new TextDecoder().decode(decrypted);
    } catch {
      throw new EncryptionError('DECRYPT_FAILED', 'No se pudo descifrar la respuesta');
    }
  }
}

function requireCrypto(): Crypto {
  if (!globalThis.crypto?.subtle) {
    throw new EncryptionError(
      'CRYPTO_UNAVAILABLE',
      'WebCrypto no está disponible (requiere https o localhost)'
    );
  }
  return globalThis.crypto;
}

async function importAesKey(keyB64: string, usage: KeyUsage): Promise<CryptoKey> {
  const cleaned = cleanBase64(keyB64);
  if (!cleaned) {
    throw new EncryptionError('KEY_MISSING', 'Falta la llave AES');
  }
  if (!BASE64_PATTERN.test(cleaned)) {
    throw new EncryptionError('KEY_INVALID', 'La llave AES no es Base64 válido');
  }
  const keyBytes = base64ToBytes(cleaned);
  if (!VALID_KEY_BYTES.includes(keyBytes.length)) {
    throw new EncryptionError('KEY_INVALID', 'La llave AES debe medir 16 o 32 bytes');
  }
  try {
    return await requireCrypto().subtle.importKey('raw', keyBytes, { name: ALGORITHM }, false, [
      usage,
    ]);
  } catch (error) {
    throw error instanceof EncryptionError
      ? error
      : new EncryptionError('KEY_INVALID', 'No se pudo importar la llave AES');
  }
}

function decodeIv(ivB64: string): Uint8Array {
  const cleaned = cleanBase64(ivB64);
  const iv = cleaned && BASE64_PATTERN.test(cleaned) ? base64ToBytes(cleaned) : undefined;
  if (!iv || iv.length !== IV_BYTES) {
    throw new EncryptionError('KEY_INVALID', 'El IV debe medir 16 bytes');
  }
  return iv;
}

function cleanBase64(value: string): string {
  return (value || '').replace(/\s+/g, '');
}

function base64ToBytes(base64: string) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}
